import { LitElement, html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';

import { widgetStyles } from './styles/widget-styles';
import { AudioEngine, type StemGainState } from './audio/engine';
import { LoadError, loadModel } from './audio/loader';
import { getModel, MODELS, type ModelDef, type StemDef } from './state/models';
import { WaveformRenderer } from './ui/waveform';
import { renderStemRow } from './ui/stem-row';
import { renderModelPicker } from './ui/model-picker';
import { renderTransport } from './ui/transport';
import { renderErrorPanel } from './ui/error-panel';
import { installKeyboardHandlers } from './a11y/keyboard';
import { emit } from './analytics/datalayer';

type Status = 'idle' | 'loading' | 'ready' | 'error';

const DEFAULT_DOWNLOAD_URL = 'https://borisfx.com/products/crumplepop/';
const DEFAULT_STEMS_URL = './widget-stems/';
const SLIDER_DEBOUNCE_MS = 500;

@customElement('soundapp-widget')
export class SoundAppWidget extends LitElement {
  static override styles = widgetStyles;

  @property({ type: String })
  model = 'cinematic';

  @property({ type: String, attribute: 'stems-url' })
  stemsUrl = DEFAULT_STEMS_URL;

  @property({ type: String, attribute: 'download-url' })
  downloadUrl = DEFAULT_DOWNLOAD_URL;

  @state() private status: Status = 'idle';
  @state() private currentTimeS = 0;
  @state() private isPlaying = false;
  @state() private stemStates: StemGainState[] = [];
  @state() private announcement = '';
  @state() private hasBeenReady = false;

  @query('canvas.waveform') private waveformCanvas?: HTMLCanvasElement;
  @query('.playhead') private playheadEl?: HTMLElement;

  private engine: AudioEngine | null = null;
  private renderer: WaveformRenderer | null = null;
  private io: IntersectionObserver | null = null;
  private uninstallKeyboard: (() => void) | null = null;
  private engineUnsubscribers: Array<() => void> = [];
  private loadAbort: AbortController | null = null;
  private currentModel: ModelDef;
  private decodeStartMs = 0;
  private firstPlayEmitted = false;
  private sliderDebounceTimers = new Map<string, number>();

  constructor() {
    super();
    this.currentModel = getModel('cinematic');
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.currentModel = getModel(this.model);
    emit('soundapp_widget_mount', { model: this.model });

    this.uninstallKeyboard = installKeyboardHandlers(this, {
      onTogglePlay: () => this.togglePlay(),
    });

    // Modality tracking: the native <select> shows :focus-visible after mouse
    // clicks in Safari (and sometimes Chrome) even though it shouldn't per
    // spec. Track whether the last input was keyboard vs. mouse and gate the
    // focus ring via CSS on data-modality.
    this.setAttribute('data-modality', 'mouse');
    document.addEventListener('keydown', this.handleKeyModality, true);
    document.addEventListener('pointerdown', this.handlePointerModality, true);

    this.setupViewportTrigger();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.uninstallKeyboard?.();
    document.removeEventListener('keydown', this.handleKeyModality, true);
    document.removeEventListener('pointerdown', this.handlePointerModality, true);
    this.io?.disconnect();
    this.io = null;
    this.loadAbort?.abort();
    this.renderer?.dispose();
    this.renderer = null;
    for (const unsub of this.engineUnsubscribers) unsub();
    this.engineUnsubscribers = [];
    for (const t of this.sliderDebounceTimers.values()) window.clearTimeout(t);
    this.sliderDebounceTimers.clear();
    this.engine?.dispose();
    this.engine = null;
  }

  private handleKeyModality = (e: KeyboardEvent): void => {
    // Only navigational keys promote to keyboard modality.
    if (e.key === 'Tab' || e.key.startsWith('Arrow') || e.key === ' ' || e.key === 'Enter') {
      this.setAttribute('data-modality', 'keyboard');
    }
  };

  private handlePointerModality = (e: PointerEvent): void => {
    this.setAttribute('data-modality', 'mouse');
    // If the user clicks anywhere that isn't the currently-focused select,
    // release the select's focus so stale focus rings can't linger after
    // the native dropdown closes.
    const active = this.shadowRoot?.activeElement as HTMLElement | null;
    if (active instanceof HTMLSelectElement && !e.composedPath().includes(active)) {
      active.blur();
    }
  };

  override updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    if (this.waveformCanvas && !this.renderer) {
      this.renderer = new WaveformRenderer(this.waveformCanvas, {
        seek: (t) => this.handleScrub(t),
      });
      this.renderer.setStems(this.currentModel.stems);
    }
    this.updatePlayhead();
  }

  private setupViewportTrigger(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.startLoading();
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.io?.disconnect();
            this.io = null;
            this.startLoading();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    this.io.observe(this);
  }

  private async startLoading(): Promise<void> {
    if (this.status === 'loading' || this.status === 'ready') return;
    return this.loadCurrentModel();
  }

  // Loads whatever this.currentModel points at. Used for both initial load and
  // model switch. Always aborts any in-flight load first and starts fresh.
  private async loadCurrentModel(): Promise<void> {
    this.loadAbort?.abort();
    const abort = new AbortController();
    this.loadAbort = abort;
    this.status = 'loading';
    this.decodeStartMs = performance.now();
    const modelId = this.currentModel.id;

    try {
      if (!this.engine) {
        this.engine = new AudioEngine();
        this.attachEngineListeners();
      }
      const loaded = await loadModel(
        this.engine.context,
        this.stemsUrl,
        this.currentModel,
        abort.signal,
      );
      // Another load may have superseded us while we awaited.
      if (abort.signal.aborted) return;
      await this.engine.setBuffers(loaded.buffers);
      // Swap renderer state in one microtask: stems, peaks, duration, and
      // states (from the engine 'ready' event fired inside setBuffers). All
      // scheduleRender calls coalesce into a single RAF paint so the
      // old→new transition is one atomic frame — no black flash.
      this.renderer?.setStems(this.currentModel.stems);
      this.renderer?.setDuration(loaded.duration);
      this.renderer?.setPeaks(loaded.peaks);
      emit('soundapp_widget_peaks_loaded', {
        model: modelId,
        bytes: loaded.peakBytes,
        parse_ms: Math.round(loaded.peakParseMs),
      });
      this.status = 'ready';
      this.hasBeenReady = true;
      this.announcement = 'Demo ready';
      const decodeMs = Math.round(performance.now() - this.decodeStartMs);
      emit('soundapp_widget_ready', { model: modelId, decode_ms: decodeMs });
    } catch (err) {
      if (abort.signal.aborted) return;
      const detail = err instanceof LoadError ? err.detail : String(err);
      const reason = err instanceof LoadError ? err.reason : 'unknown';
      this.status = 'error';
      this.announcement = "Couldn't load the demo";
      emit('soundapp_widget_error', { reason, detail });
    }
  }

  private attachEngineListeners(): void {
    if (!this.engine) return;
    this.engineUnsubscribers.push(
      this.engine.on('play', () => {
        this.isPlaying = true;
        this.announcement = 'Playing';
        if (!this.firstPlayEmitted) {
          this.firstPlayEmitted = true;
          emit('soundapp_widget_play', { model: this.model });
        }
      }),
      this.engine.on('pause', () => {
        this.isPlaying = false;
        this.announcement = 'Paused';
        emit('soundapp_widget_pause', {
          model: this.model,
          position_s: Number(this.currentTimeS.toFixed(3)),
        });
      }),
      this.engine.on('timeupdate', () => {
        this.currentTimeS = this.engine!.currentTime;
        this.updatePlayhead();
      }),
      this.engine.on('ready', () => {
        this.stemStates = this.currentModel.stems.map(
          (_, i) => ({ ...this.engine!.getStemState(i) }),
        );
        this.renderer?.setStates(this.stemStates);
      }),
    );
  }

  private updatePlayhead(): void {
    if (!this.playheadEl || !this.engine) return;
    const duration = this.engine.duration;
    if (duration <= 0) {
      this.playheadEl.style.left = '0%';
      this.playheadEl.style.opacity = '0';
      return;
    }
    const frac = Math.max(0, Math.min(1, this.currentTimeS / duration));
    // Percentage-based so the playhead tracks the host's current width even
    // when the window is resized between updates — no stale pixel values.
    this.playheadEl.style.left = `${frac * 100}%`;
    this.playheadEl.style.opacity = '1';
  }

  private async togglePlay(): Promise<void> {
    if (!this.engine || this.status !== 'ready') return;
    if (this.engine.isPlaying) {
      this.engine.pause();
    } else {
      await this.engine.play();
    }
    // Release focus from any non-button element so stray focus rings from a
    // previous scrub / dropdown / slider don't linger on the canvas, select,
    // or other focusable controls.
    const active = this.shadowRoot?.activeElement as HTMLElement | null;
    if (active && active.tagName !== 'BUTTON') {
      active.blur();
    }
  }

  private handleScrub(seconds: number): void {
    if (!this.engine || this.status !== 'ready') return;
    const from = this.currentTimeS;
    this.engine.seek(seconds);
    this.announcement = `Seeked to ${seconds.toFixed(1)} seconds`;
    emit('soundapp_widget_scrub', {
      model: this.model,
      from_s: Number(from.toFixed(3)),
      to_s: Number(seconds.toFixed(3)),
    });
  }

  private handleVolume = (stem: StemDef, value: number): void => {
    if (!this.engine) return;
    const idx = this.stemIndex(stem);
    this.engine.setStemVolume(idx, value);
    this.stemStates = this.stemStates.map((s, i) =>
      i === idx ? { ...s, volume: value } : s,
    );
    this.renderer?.setStates(this.stemStates);

    const prev = this.sliderDebounceTimers.get(stem.name);
    if (prev) window.clearTimeout(prev);
    const timer = window.setTimeout(() => {
      emit('soundapp_widget_slider_adjust', {
        model: this.model,
        stem: stem.name,
        value: Number(value.toFixed(2)),
      });
      this.sliderDebounceTimers.delete(stem.name);
    }, SLIDER_DEBOUNCE_MS);
    this.sliderDebounceTimers.set(stem.name, timer);
  };

  private handleToggleMute = (stem: StemDef): void => {
    if (!this.engine) return;
    const idx = this.stemIndex(stem);
    const wasMuted = this.stemStates[idx]?.muted ?? false;
    const nextMuted = !wasMuted;
    this.engine.setStemMuted(idx, nextMuted);
    this.stemStates = this.stemStates.map((_, i) => ({
      ...this.engine!.getStemState(i),
    }));
    this.renderer?.setStates(this.stemStates);
    emit('soundapp_widget_mute_toggle', {
      model: this.model,
      stem: stem.name,
      muted: nextMuted,
    });
  };

  private handleToggleSolo = (stem: StemDef): void => {
    if (!this.engine) return;
    const idx = this.stemIndex(stem);
    const wasSoloed = this.stemStates[idx]?.soloed ?? false;
    this.engine.setStemSoloed(idx, !wasSoloed);
    this.stemStates = this.stemStates.map((_, i) => ({
      ...this.engine!.getStemState(i),
    }));
    this.renderer?.setStates(this.stemStates);
    emit('soundapp_widget_solo_toggle', {
      model: this.model,
      stem: stem.name,
      soloed: !wasSoloed,
    });
  };

  private handleModelChange = async (newId: string): Promise<void> => {
    const prev = this.currentModel.id;
    if (newId === prev) return;
    emit('soundapp_widget_model_change', { from: prev, to: newId });

    // Pause current playback and reset play-tracking so the new model's first
    // play event is emitted fresh.
    this.engine?.pause();
    this.firstPlayEmitted = false;
    this.currentTimeS = 0;

    // Swap the model (panel shows new stem list at default values, disabled
    // while loading). Leave the renderer's stems/peaks/states untouched so
    // the previous model's waveform remains painted until new peaks arrive.
    // The atomic swap happens at the end of loadCurrentModel.
    this.currentModel = getModel(newId);
    this.stemStates = this.currentModel.stems.map(() => ({
      volume: 1,
      muted: false,
      soloed: false,
    }));

    await this.loadCurrentModel();
  };

  private stemIndex(stem: StemDef): number {
    return this.currentModel.stems.findIndex((s) => s.name === stem.name);
  }

  override render(): TemplateResult {
    const options = Object.values(MODELS);
    const duration = this.engine?.duration ?? 0;
    const anySoloed = this.stemStates.some((s) => s?.soloed);
    const controlsDisabled = this.status !== 'ready';
    // Play button is disabled during any non-ready state (initial load AND
    // model-switch load). The visible loading overlay over .left provides the
    // primary "we're loading" signal; the disabled play button prevents
    // silent no-op clicks during model switches.
    const playDisabled = this.status !== 'ready';
    const showLoadingOverlay = this.status === 'loading';

    const card =
      this.status === 'error'
        ? html`<div class="card"><div class="error-panel-host" style="grid-column: 1 / -1">${
            renderErrorPanel(this.downloadUrl, () => this.retry())
          }</div></div>`
        : html`
            <div class="card" part="card">
              <div class="left">
                <div class="waveform-host">
                  <canvas
                    class="waveform"
                    tabindex="0"
                    role="slider"
                    aria-label="Playback position"
                    aria-valuemin="0"
                    aria-valuemax=${Math.max(0, duration)}
                    aria-valuenow=${this.currentTimeS}
                    aria-valuetext=${`${this.currentTimeS.toFixed(1)} of ${duration.toFixed(1)} seconds`}
                  ></canvas>
                  <div class="playhead" aria-hidden="true"></div>
                </div>
                ${renderTransport(
                  this.isPlaying,
                  this.currentTimeS,
                  duration,
                  // Initial-load only: the in-button spinner doubles up with
                  // the overlay during the first paint. On subsequent model
                  // switches, the centered overlay carries the load indicator
                  // alone and the button just goes disabled.
                  this.status === 'loading' && !this.hasBeenReady,
                  playDisabled,
                  { onToggle: () => this.togglePlay() },
                )}
                ${showLoadingOverlay
                  ? html`
                      <div class="waveform-loading" aria-live="polite">
                        <span class="spinner" aria-hidden="true"></span>
                        <span class="waveform-loading-label">Loading audio…</span>
                      </div>
                    `
                  : nothing}
              </div>
              <div class="panel">
                ${renderModelPicker(options, this.currentModel.id, this.handleModelChange)}
                <div class="stems ${controlsDisabled ? 'dimmed' : ''}">
                  ${this.currentModel.stems.map((stem, i) =>
                    renderStemRow(
                      stem,
                      this.stemStates[i],
                      anySoloed,
                      controlsDisabled,
                      {
                        onVolume: this.handleVolume,
                        onToggleMute: this.handleToggleMute,
                        onToggleSolo: this.handleToggleSolo,
                      },
                    ),
                  )}
                </div>
              </div>
            </div>
          `;

    return html`
      <div
        class="too-narrow"
        role="img"
        aria-label="SoundApp demo requires a wider viewport"
      >
        SoundApp demo requires a wider viewport.
      </div>
      ${card}
      <div class="sr-only" role="status" aria-live="polite">${this.announcement}</div>
      ${nothing}
    `;
  }

  private retry(): void {
    this.status = 'idle';
    this.startLoading();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'soundapp-widget': SoundAppWidget;
  }
}
